import { LogoutOptions, useAuth0 } from '@auth0/auth0-react';
import { usePostHog } from '@posthog/react';
import { Button } from '@talentmesh/ui/components/button';
import { Building2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, LogOut, Settings } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import ProfileMenu from './ProfileMenu';
import useProfileMenuButton from './utils/useProfileMenuButton';
import { authProviderSettings, siteUrls } from '@/AppSettings';
import { useCompanyContext } from '@/Context/CompanyContext';
import { useUserContext } from '@/Context/UserContext';
import { MenuViewItem, MenuViews } from '@/Models/ProfileMenu';
import RoutePath from '@/Routing/RoutePath';
import useTalentMeshHistory from '@/Routing/useTalentMeshHistory';
import UIStrings from '@/Utils/UIStrings';

const ProfileMenuButton: React.FC = () => {
    const { memberships } = useUserContext();
    const { currentCompanyProfile, refreshCurrentCompanyProfile } = useCompanyContext();
    const { logout } = useAuth0();
    const navigate = useNavigate();
    const tmHistory = useTalentMeshHistory();
    const { open, handleOpenChange, currentView, setCurrentView } = useProfileMenuButton();
    const posthog = usePostHog();
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useCallback(
        (node: HTMLSpanElement) => {
            setIsOverflowing(node?.scrollWidth > node?.clientWidth);
        },
        [setIsOverflowing],
    );

    const logoutHandler = useCallback(() => {
        const logoutOptions: LogoutOptions = {
            client_id: authProviderSettings.clientId,
            returnTo: siteUrls.homePageUrl,
        };
        posthog.reset(true);
        logout(logoutOptions);
    }, [logout, posthog]);

    const settingsHandler = useCallback(() => {
        tmHistory.push(RoutePath.AccountSettingsGeneral);
    }, [tmHistory]);

    const menuViewItems = useMemo<MenuViewItem[]>(
        () => [
            {
                viewId: MenuViews.Main,
                menuItems: [
                    {
                        menuItemLabel: UIStrings.SwitchCompany,
                        icon: <Building2 />,
                        onClick: () => setCurrentView(MenuViews.Companies),
                        menuItemChildren: <ChevronRight />,
                    },
                    {
                        menuItemLabel: UIStrings.Settings,
                        icon: <Settings />,
                        onClick: settingsHandler,
                    },
                    {
                        menuItemLabel: UIStrings.Logout,
                        icon: <LogOut />,
                        onClick: logoutHandler,
                    },
                ],
            },
            {
                viewId: MenuViews.Companies,
                menuItems: [
                    {
                        menuItemLabel: UIStrings.Back,
                        icon: <ChevronLeft />,
                        onClick: () => setCurrentView(MenuViews.Main),
                    },
                    ...memberships.map(({ companyId, companyName }) => ({
                        id: companyId,
                        menuItemLabel: companyName,
                        onClick: async () => {
                            await refreshCurrentCompanyProfile(companyId);
                            navigate(generatePath(RoutePath.CompanyRoot, { companyId }));
                        },
                        labelClassName: 'overflow-hidden text-ellipsis whitespace-nowrap',
                    })),
                ],
            },
        ],
        [logoutHandler, memberships, navigate, refreshCurrentCompanyProfile, setCurrentView, settingsHandler],
    );

    return (
        <ProfileMenu
            open={open}
            onOpenChange={handleOpenChange}
            trigger={
                <Button
                    type="button"
                    variant="ghost"
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    className="h-auto max-w-56 px-3 py-2 font-semibold"
                    onClick={(e) => e.stopPropagation()}
                >
                    <span data-cy="profileMenuCompanyName" className="min-w-0 flex-1 truncate text-left" ref={textRef}>
                        {currentCompanyProfile.companyName}
                    </span>
                    {open ? (
                        <ChevronUp className="size-4 shrink-0 stroke-3 opacity-70" />
                    ) : (
                        <ChevronDown className="size-4 shrink-0 stroke-3 opacity-70" />
                    )}
                </Button>
            }
            menuViewItems={menuViewItems}
            currentView={currentView}
            tooltipContent={isOverflowing && currentCompanyProfile.companyName}
        />
    );
};

export default ProfileMenuButton;
